import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import PiscinaBuscador from '../components/PiscinaBuscador';
import { useFincaId } from '../context/FincaContext';
import { format } from 'date-fns';
import ReporteCosecha from './ReporteCosecha';

export default function Cosechas() {
  const fincaIdContext = useFincaId();
  const [searchParams] = useSearchParams();
  const tabActiva = searchParams.get('tab') || 'gestion';

  const [cosechas, setCosechas] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [piscinas, setPiscinas] = useState([]);
  const [corridas, setCorridas] = useState([]);

  const [sectorFiltro, setSectorFiltro] = useState('');
  const [piscinaFiltro, setPiscinaFiltro] = useState('');
  const [siembraFiltro, setSiembraFiltro] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');

  const [mostrarModal, setMostrarModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [cargando, setCargando] = useState(true);

  const [form, setForm] = useState({
    sector_id: '',
    piscina_id: '',
    siembra_id: '',
    fecha: new Date().toISOString().split('T')[0],
    peso_g: '',
    libras: '',
    unidad_biomasa: 'lb',
    tipo: 'cosecha',
    observaciones: '',
  });

  useEffect(() => {
    cargarInicial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaIdContext]);

  useEffect(() => {
    if (form.sector_id) {
      cargarPiscinas(form.sector_id, true);
    } else {
      setPiscinas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.sector_id]);

  useEffect(() => {
    if (form.piscina_id) {
      cargarCorridas(form.piscina_id);
    } else {
      setCorridas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.piscina_id]);

  useEffect(() => {
    if (piscinaFiltro) {
      cargarCorridas(piscinaFiltro);
    } else {
      setCorridas([]);
    }
    setSiembraFiltro('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piscinaFiltro]);

  useEffect(() => {
    if (sectorFiltro) {
      cargarPiscinasFiltro(sectorFiltro);
    } else {
      setPiscinas([]);
      setPiscinaFiltro('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorFiltro]);

  const diasCultivo = useMemo(() => {
    if (!form.fecha || !form.siembra_id) return null;
    const siembra = corridas.find((c) => c.id === form.siembra_id);
    if (!siembra?.fecha_siembra) return null;
    const f1 = new Date(form.fecha);
    const f2 = new Date(siembra.fecha_siembra);
    const diff = Math.floor((f1.getTime() - f2.getTime()) / (1000 * 60 * 60 * 24));
    return diff < 0 ? 0 : diff;
  }, [form.fecha, form.siembra_id, corridas]);

  const cargarInicial = async () => {
    setCargando(true);
    try {
      if (!fincaIdContext) {
        setSectores([]);
        return;
      }
      const { data: secs } = await api.get(`/sectores/finca/${fincaIdContext}`);
      setSectores(secs || []);
      await cargarCosechas();
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'Error al cargar' });
    } finally {
      setCargando(false);
    }
  };

  const cargarCosechas = async () => {
    try {
      const params = {};
      if (sectorFiltro) params.sector_id = sectorFiltro;
      if (piscinaFiltro) params.piscina_id = piscinaFiltro;
      if (siembraFiltro) params.siembra_id = siembraFiltro;
      if (tipoFiltro) params.tipo = tipoFiltro;
      const { data } = await api.get('/cosechas', { params });
      setCosechas(data);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'Error al cargar cosechas' });
    }
  };

  const cargarPiscinas = async (sectorId, incluirInactivas = false) => {
    try {
      const url = incluirInactivas
        ? `/piscinas/sector/${sectorId}?incluirInactivas=true`
        : `/piscinas/sector/${sectorId}`;
      const { data } = await api.get(url);
      setPiscinas(data);
    } catch (err) {
      console.error(err);
    }
  };

  const cargarCorridas = async (piscinaId) => {
    try {
      const { data } = await api.get(`/siembras/piscina/${piscinaId}`);
      setCorridas(data);
    } catch (err) {
      console.error(err);
      setCorridas([]);
    }
  };

  const cargarPiscinasFiltro = async (sectorId) => {
    try {
      const { data } = await api.get(`/piscinas/sector/${sectorId}?incluirInactivas=true`);
      setPiscinas(data);
    } catch (err) {
      console.error(err);
    }
  };

  const aplicarFiltros = () => cargarCosechas();

  const limpiarFiltros = () => {
    setSectorFiltro('');
    setPiscinaFiltro('');
    setSiembraFiltro('');
    setTipoFiltro('');
    setTimeout(cargarCosechas, 100);
  };

  const abrirNuevo = () => {
    setEditando(null);
    setForm({
      sector_id: sectores[0]?.id || '',
      piscina_id: '',
      siembra_id: '',
      fecha: new Date().toISOString().split('T')[0],
      peso_g: '',
      libras: '',
      unidad_biomasa: 'lb',
      tipo: 'cosecha',
      observaciones: '',
    });
    setMostrarModal(true);
  };

  const abrirEditar = (c) => {
    setEditando(c);
    setForm({
      sector_id: c.sector_id || '',
      piscina_id: c.piscina_id || '',
      siembra_id: c.siembra_id || '',
      fecha: c.fecha ? c.fecha.split('T')[0] : '',
      peso_g: c.peso_g != null ? String(c.peso_g) : '',
      libras: c.libras != null ? String(c.libras) : '',
      unidad_biomasa: c.unidad_biomasa || 'lb',
      tipo: c.tipo,
      observaciones: c.observaciones || '',
    });
    setMostrarModal(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setMensaje(null);
    try {
      const payload = {
        piscina_id: form.piscina_id,
        siembra_id: form.siembra_id || null,
        fecha: form.fecha,
        peso_g: form.peso_g ? parseFloat(form.peso_g) : null,
        libras: form.libras ? parseFloat(form.libras) : null,
        unidad_biomasa: form.unidad_biomasa || 'lb',
        tipo: form.tipo,
        observaciones: form.observaciones || null,
      };

      if (editando) {
        await api.put(`/cosechas/${editando.id}`, payload);
      } else {
        await api.post('/cosechas', payload);
      }
      setMostrarModal(false);
      await cargarCosechas();
      setMensaje({
        tipo: 'ok',
        texto: editando
          ? 'Registro actualizado'
          : form.tipo === 'cosecha'
            ? 'Cosecha registrada. La piscina se desactivó automáticamente.'
            : 'Raleo registrado',
      });
    } catch (err) {
      setMensaje({
        tipo: 'error',
        texto: err.response?.data?.error || 'Error al guardar',
      });
    }
  };

  const eliminar = async (c) => {
    if (!confirm(`¿Eliminar este ${c.tipo} del ${format(new Date(c.fecha), 'dd/MM/yyyy')}?`)) return;
    try {
      await api.delete(`/cosechas/${c.id}`);
      await cargarCosechas();
      setMensaje({ tipo: 'ok', texto: 'Registro eliminado' });
    } catch (err) {
      setMensaje({ tipo: 'error', texto: 'Error al eliminar' });
    }
  };

  const totalRaleos = cosechas.filter(c => c.tipo === 'raleo').length;
  const totalCosechas = cosechas.filter(c => c.tipo === 'cosecha').length;

  return (
    <div className="flex flex-col h-full w-full">

      {/* --- CONTENIDO SEGÚN PESTAÑA --- */}
      <div className="flex-1 overflow-y-auto">
        {tabActiva === 'reporte' && <ReporteCosecha />}

        {tabActiva === 'gestion' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <h1 className="text-3xl font-bold text-primary">Gestión de Cosechas</h1>
              <button
                onClick={abrirNuevo}
                className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-800"
              >
                + Nuevo registro
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              <div className="bg-white p-4 rounded shadow">
                <div className="text-xs text-gray-500">Total registros</div>
                <div className="text-2xl font-bold">{cosechas.length}</div>
              </div>
              <div className="bg-white p-4 rounded shadow">
                <div className="text-xs text-gray-500">Raleos</div>
                <div className="text-2xl font-bold text-orange-600">{totalRaleos}</div>
              </div>
              <div className="bg-white p-4 rounded shadow">
                <div className="text-xs text-gray-500">Cosechas</div>
                <div className="text-2xl font-bold text-green-700">{totalCosechas}</div>
              </div>
            </div>

            {/* Filtros */}
            <div className="bg-white p-4 rounded shadow mb-4 flex flex-wrap gap-3 items-end">
              <label className="flex flex-col">
                <span className="text-xs font-medium text-gray-600 mb-1">Sector</span>
                <select
                  value={sectorFiltro}
                  onChange={(e) => setSectorFiltro(e.target.value)}
                  className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                >
                  <option value="">Todos</option>
                  {sectores.map((s) => (
                    <option key={s.id} value={s.id}>{s.nombre}</option>
                  ))}
                </select>
              </label>

              <div className="flex flex-col min-w-[180px]">
                <PiscinaBuscador
                  piscinas={piscinas}
                  value={piscinaFiltro}
                  onChange={(id) => setPiscinaFiltro(id)}
                  disabled={!sectorFiltro}
                  permitirTodas={true}
                  valorTodas=""
                  labelTodas="Todas las piscinas"
                />
              </div>

              <label className="flex flex-col">
                <span className="text-xs font-medium text-gray-600 mb-1">Corrida</span>
                <select
                  value={siembraFiltro}
                  onChange={(e) => setSiembraFiltro(e.target.value)}
                  className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm min-w-[150px]"
                  disabled={!piscinaFiltro || corridas.length === 0}
                >
                  <option value="">Todas las corridas</option>
                  {corridas.map((c) => (
                    <option key={c.id} value={c.id}>
                      #{c.numero_corrida || '?'} - {format(new Date(c.fecha_siembra), 'dd/MM/yyyy')}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col">
                <span className="text-xs font-medium text-gray-600 mb-1">Tipo</span>
                <select
                  value={tipoFiltro}
                  onChange={(e) => setTipoFiltro(e.target.value)}
                  className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                >
                  <option value="">Todos</option>
                  <option value="raleo">Raleo</option>
                  <option value="cosecha">Cosecha</option>
                </select>
              </label>

              <button
                onClick={aplicarFiltros}
                className="px-4 py-2 bg-primary text-white rounded text-sm hover:bg-blue-800"
              >
                Filtrar
              </button>
              <button
                onClick={limpiarFiltros}
                className="px-4 py-2 border rounded text-sm hover:bg-gray-100"
              >
                Limpiar
              </button>
            </div>

            {mensaje && (
              <div className={`mb-4 p-3 rounded ${mensaje.tipo === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {mensaje.texto}
              </div>
            )}

            {/* Tabla */}
            {cargando ? (
              <div className="bg-white p-6 rounded shadow text-center text-gray-500">Cargando...</div>
            ) : cosechas.length === 0 ? (
              <div className="bg-white p-6 rounded shadow text-center text-gray-500">
                No hay registros con estos filtros.
              </div>
            ) : (
              <div className="bg-white rounded shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left px-3 py-3 font-semibold">Fecha</th>
                        <th className="text-center px-3 py-3 font-semibold">Días Cultivo</th>
                        <th className="text-left px-3 py-3 font-semibold">Tipo</th>
                        <th className="text-left px-3 py-3 font-semibold">Sector</th>
                        <th className="text-left px-3 py-3 font-semibold">Piscina</th>
                        <th className="text-center px-3 py-3 font-semibold">Corrida</th>
                        <th className="text-right px-3 py-3 font-semibold">Peso (g)</th>
                        <th className="text-right px-3 py-3 font-semibold">Biomasa cosechada</th>
                        <th className="text-left px-3 py-3 font-semibold">Usuario</th>
                        <th className="text-right px-3 py-3 font-semibold">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cosechas.map((c) => (
                        <tr key={c.id} className="border-t hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap">
                            {format(new Date(c.fecha), 'dd/MM/yyyy')}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {c.dias_cultivo != null ? (
                              <span className="inline-block px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded text-xs font-semibold">
                                {c.dias_cultivo} d
                              </span>
                            ) : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                              c.tipo === 'raleo'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {c.tipo === 'raleo' ? 'Raleo' : 'Cosecha'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-600">{c.sector_nombre}</td>
                          <td className="px-3 py-2 font-medium">{c.piscina_codigo}</td>
                          <td className="px-3 py-2 text-center">
                            {c.numero_corrida != null ? (
                              <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                                #{c.numero_corrida}
                              </span>
                            ) : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {c.peso_g != null ? Number(c.peso_g).toFixed(2) : '-'}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {c.libras != null
                              ? `${Number(c.libras).toFixed(2)} ${c.unidad_biomasa || 'lb'}`
                              : '-'}
                          </td>
                          <td className="px-3 py-2 text-gray-600 text-xs">{c.usuario_nombre || '-'}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <button
                              onClick={() => abrirEditar(c)}
                              className="text-blue-600 hover:underline mr-3 text-xs"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => eliminar(c)}
                              className="text-red-600 hover:underline text-xs"
                            >
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Modal */}
            {mostrarModal && (
              <div
                className="fixed inset-0 md:left-64 z-[9999] flex items-center justify-center p-4"
                style={{
                  backgroundColor: 'rgba(75, 85, 99, 0.6)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                }}
              >
                <div
                  className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
                  style={{
                    border: '2px solid #0D47A1',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                  }}
                >
                  <div
                    className="px-5 py-4 text-white flex items-center justify-between"
                    style={{
                      background: 'linear-gradient(to right, #0D47A1, #1565C0)',
                      borderBottom: '2px solid #0D47A1',
                    }}
                  >
                    <div>
                      <h2 className="text-sm font-bold">
                        {editando ? 'Editar registro' : 'Nuevo registro'}
                      </h2>
                      <p className="text-xs" style={{ color: '#BBDEFB' }}>
                        Raleo o Cosecha
                      </p>
                    </div>
                    <button
                      onClick={() => setMostrarModal(false)}
                      className="rounded-full w-8 h-8 flex items-center justify-center transition"
                      style={{
                        color: 'rgba(255,255,255,0.9)',
                        border: '1px solid rgba(255,255,255,0.3)',
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  <form onSubmit={guardar} className="flex-1 overflow-y-auto">
                    <div className="px-5 py-4 space-y-4">
                      <div className="grid grid-cols-2 gap-2">
                        <label className="block col-span-2">
                          <span className="text-xs text-gray-500">Tipo</span>
                          <div className="flex gap-2 mt-1">
                            <button
                              type="button"
                              onClick={() => setForm({ ...form, tipo: 'raleo' })}
                              className={`flex-1 py-2 rounded-lg text-sm font-medium transition border ${
                                form.tipo === 'raleo'
                                  ? 'bg-orange-500 text-white border-orange-500'
                                  : 'bg-white text-gray-700 border-gray-300'
                              }`}
                            >
                              🎣 Raleo
                            </button>
                            <button
                              type="button"
                              onClick={() => setForm({ ...form, tipo: 'cosecha' })}
                              className={`flex-1 py-2 rounded-lg text-sm font-medium transition border ${
                                form.tipo === 'cosecha'
                                  ? 'bg-green-600 text-white border-green-600'
                                  : 'bg-white text-gray-700 border-gray-300'
                              }`}
                            >
                              🦐 Cosecha
                            </button>
                          </div>
                        </label>

                        <label className="block">
                          <span className="text-xs text-gray-500">Sector</span>
                          <select
                            value={form.sector_id}
                            onChange={(e) => setForm({ ...form, sector_id: e.target.value, piscina_id: '', siembra_id: '' })}
                            className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm bg-white"
                            required
                          >
                            <option value="">-- Selecciona --</option>
                            {sectores.map((s) => (
                              <option key={s.id} value={s.id}>{s.nombre}</option>
                            ))}
                          </select>
                        </label>

                        <div className="block">
                          <PiscinaBuscador
                            piscinas={piscinas}
                            value={form.piscina_id}
                            onChange={(id) => setForm({ ...form, piscina_id: id, siembra_id: '' })}
                            disabled={!form.sector_id}
                          />
                        </div>

                        <label className="block col-span-2">
                          <span className="text-xs text-gray-500">Corrida</span>
                          <select
                            value={form.siembra_id}
                            onChange={(e) => setForm({ ...form, siembra_id: e.target.value })}
                            className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm bg-white"
                            disabled={!form.piscina_id || corridas.length === 0}
                          >
                            <option value="">
                              {corridas.length === 0
                                ? 'Esta piscina no tiene corridas registradas'
                                : '-- Selecciona --'}
                            </option>
                            {corridas.map((c) => (
                              <option key={c.id} value={c.id}>
                                #{c.numero_corrida || '?'} - Sembrado el {format(new Date(c.fecha_siembra), 'dd/MM/yyyy')}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="block">
                          <span className="text-xs text-gray-500">Fecha</span>
                          <input
                            type="date"
                            value={form.fecha}
                            onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                            className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                            required
                          />
                        </label>

                        <label className="block">
                          <span className="text-xs text-gray-500">Días de cultivo</span>
                          <input
                            type="text"
                            value={diasCultivo != null ? `${diasCultivo} días` : '—'}
                            readOnly
                            className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 text-sm cursor-not-allowed"
                          />
                        </label>

                        <label className="block">
                          <span className="text-xs text-gray-500">Peso (g)</span>
                          <input
                            type="number"
                            step="0.01"
                            value={form.peso_g}
                            onChange={(e) => setForm({ ...form, peso_g: e.target.value })}
                            placeholder="0.00"
                            className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                          />
                        </label>

                        <label className="block">
                          <span className="text-xs text-gray-500">Biomasa cosechada</span>
                          <input
                            type="number"
                            step="0.01"
                            value={form.libras}
                            onChange={(e) => setForm({ ...form, libras: e.target.value })}
                            placeholder="0.00"
                            className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                          />
                        </label>

                        <label className="block">
                          <span className="text-xs text-gray-500">Unidad</span>
                          <select
                            value={form.unidad_biomasa}
                            onChange={(e) => setForm({ ...form, unidad_biomasa: e.target.value })}
                            className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm bg-white"
                          >
                            <option value="lb">Libras</option>
                            <option value="kg">Kilos</option>
                          </select>
                        </label>

                        <label className="block col-span-2">
                          <span className="text-xs text-gray-500">Observaciones</span>
                          <textarea
                            value={form.observaciones}
                            onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                            rows={2}
                            className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm resize-none"
                            placeholder="Opcional"
                          />
                        </label>
                      </div>
                    </div>

                    <div
                      className="px-5 py-3 flex justify-center gap-3"
                      style={{ backgroundColor: '#F9FAFB', borderTop: '1px solid #E5E7EB' }}
                    >
                      <button
                        type="button"
                        onClick={() => setMostrarModal(false)}
                        className="px-5 py-1.5 rounded-lg text-sm text-gray-700 transition hover:bg-gray-200"
                        style={{ border: '1px solid #D1D5DB' }}
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="px-5 py-1.5 text-white rounded-lg text-sm font-medium transition"
                        style={{ backgroundColor: '#0D47A1' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1565C0'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0D47A1'}
                      >
                        {editando ? 'Guardar cambios' : 'Crear registro'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}